// Panel lateral de alta: título, pestañas con un `<form>` cada una y pie con Cancelar + enviar.
import { useId, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { CircleAlert, Plus } from 'lucide-react';
import { Button, Tabs } from '@/kit';
import { SideDrawer } from '@/shared/patterns/SideDrawer';

/** Aviso del error de una escritura, con el mensaje del contrato. */
export function WriteErrorNotice({ message }: { message: string | null }) {
    if (message === null) return null;
    return (
        <div
            role="alert"
            className="flex items-start gap-2 rounded-md border border-danger/30 bg-danger-soft px-3 py-2 text-[11.5px] text-danger"
        >
            <CircleAlert className="mt-px size-3.5 flex-none" aria-hidden />
            <span>{message}</span>
        </div>
    );
}

/** Panel de alto completo para formularios: el cuerpo scrollea y el pie queda fijo. */
export function FormDrawer({
    open = true,
    onClose,
    title,
    footer,
    children,
}: {
    open?: boolean;
    onClose: () => void;
    title: ReactNode;
    footer: ReactNode;
    children: ReactNode;
}) {
    // Va al body para que ningún contenedor de la pantalla lo recorte ni lo tape.
    return createPortal(
        <SideDrawer
            open={open}
            onClose={onClose}
            title={title}
            footer={footer}
            className="w-[640px]"
        >
            <div className="flex flex-col gap-5">{children}</div>
        </SideDrawer>,
        document.body,
    );
}

/** Pie del panel: el aviso de error a la izquierda y las acciones a la derecha. */
export function FormDrawerFooter({
    formId,
    submitLabel,
    isPending,
    error,
    onCancel,
}: {
    formId: string;
    submitLabel: string;
    isPending: boolean;
    error: string | null;
    onCancel: () => void;
}) {
    return (
        <div className="flex w-full items-center gap-2">
            <div className="min-w-0 flex-1">
                <WriteErrorNotice message={error} />
            </div>
            <Button type="button" variant="outline" onClick={onCancel} disabled={isPending}>
                Cancelar
            </Button>
            <Button type="submit" form={formId} isLoading={isPending}>
                {submitLabel}
            </Button>
        </div>
    );
}

export interface CreateDrawerTab<TKey extends string> {
    key: TKey;
    label: string;
    /** El contenido de la pestaña; su `<form>` lleva el `formId` que recibe. */
    render: (formId: string) => ReactNode;
}

export interface CreateDrawerProps<TKey extends string> {
    title: ReactNode;
    tabs: readonly CreateDrawerTab<TKey>[];
    activeTab: TKey;
    onTabChange: (key: TKey) => void;
    submitLabel: string;
    isPending: boolean;
    error: string | null;
    onClose: () => void;
    /** Monta todas las pestañas y oculta las inactivas, para no perder lo capturado al cambiar. */
    keepMounted?: boolean;
}

/** Con una sola pestaña no pinta la tira; el botón primario envía el form de la pestaña activa. */
export function CreateDrawer<TKey extends string>({
    title,
    tabs,
    activeTab,
    onTabChange,
    submitLabel,
    isPending,
    error,
    onClose,
    keepMounted = false,
}: CreateDrawerProps<TKey>) {
    const baseId = useId();
    const formIdOf = (key: TKey) => `${baseId}-${key}`;
    return (
        <FormDrawer
            onClose={onClose}
            title={title}
            footer={
                <FormDrawerFooter
                    formId={formIdOf(activeTab)}
                    submitLabel={submitLabel}
                    isPending={isPending}
                    error={error}
                    onCancel={onClose}
                />
            }
        >
            {tabs.length > 1 ? (
                <Tabs
                    tabs={tabs.map((tab) => ({ key: tab.key, label: tab.label }))}
                    activeTab={activeTab}
                    onChange={(key) => {
                        onTabChange(key as TKey);
                    }}
                />
            ) : null}
            {tabs.map((tab) => {
                const active = tab.key === activeTab;
                if (!active && !keepMounted) return null;
                return (
                    <div key={tab.key} hidden={!active}>
                        {tab.render(formIdOf(tab.key))}
                    </div>
                );
            })}
        </FormDrawer>
    );
}

/** El botón «+ Nuevo» de la barra de una tabla. */
export function NewButton({
    label = 'Nuevo',
    disabled,
    title,
    onClick,
}: {
    label?: string;
    disabled?: boolean;
    title?: string;
    onClick: () => void;
}) {
    return (
        <Button
            type="button"
            size="sm"
            disabled={disabled}
            title={title}
            className="h-6 px-2 text-[11px]"
            leftIcon={<Plus className="size-3" aria-hidden />}
            onClick={onClick}
        >
            {label}
        </Button>
    );
}
